import { Formik } from "formik";
import { GetAllWfsLogRequest } from "../../../types/WfsLog/GetAllWfsLogRequest";
import { User } from "../../../types/User";
import styled from "styled-components";
import { DateTime } from "luxon";
import { timeSchema } from "./timeSchema";
import { Button, Form, FormSelect } from "react-bootstrap";
import UserSearch from "../../userSearch/UserSearch";
import LayerDropdown from "../../layerDropdown/LayerDropdown";
import TimeInput from "../../../form/time/timeInput";

const TimeContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

interface TimeForm {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 30% 30% 20% 20%;
  width: 100%;
  gap: 0.2rem;
`;

const LogsAfter = styled.div`
  grid-column: 1;
  grid-row: 2;
`;

const LogBefore = styled.div`
  grid-column: 2;
  grid-row: 2;
`;

const ApplyRange = styled.div`
  grid-column: 3;
  grid-row: 2;
  display: flex;
  justify-content: start;
  align-items: end;
`;

const PageOptions = styled.div`
  grid-column: 4;
  grid-row: 2;
  display: flex;
  justify-content: flex-end;
  align-items: end;
  width: 100%;
  height: 100%;
  padding-right: 0.5rem;
`;

interface RequestHistoryFiltersProps {
  isAdmin: boolean;
  filterRequest: GetAllWfsLogRequest;
  setFilterRequest: (request: GetAllWfsLogRequest) => void;
  selectedUser: User | undefined;
  setSelectedUser: (user: User | undefined) => void;
  setCurrentPage: (page: number) => void;
}

const RequestHistoryFilters = ({
  isAdmin,
  filterRequest,
  setFilterRequest,
  selectedUser,
  setSelectedUser,
  setCurrentPage,
}: RequestHistoryFiltersProps) => {
  const countOptions = [5, 10, 20, 30, 50, 100];

  const timeSubmit = (values: TimeForm) => {
    setCurrentPage(1);
    setFilterRequest({
      ...filterRequest,
      startIndex: 0,
      startTime: DateTime.fromISO(`${values.startDate}T${values.startTime}`),
      endTime: DateTime.fromISO(`${values.endDate}T${values.endTime}`),
    });
  };

  const timeValues = {
    startDate: filterRequest.startTime?.toISO()!.split("T")[0],
    startTime: filterRequest.startTime
      ?.toISO()!
      .split("T")[1]
      .split("+")[0]
      .slice(0, 8),
    endDate: filterRequest.endTime?.toISO()!.split("T")[0],
    endTime: filterRequest.endTime
      ?.toISO()!
      .split("T")[1]
      .split("+")[0]
      .slice(0, 8),
  } as TimeForm;

  return (
    <Formik
      validationSchema={timeSchema}
      onSubmit={timeSubmit}
      initialValues={timeValues}
    >
      {({ values, handleSubmit, handleChange, setFieldValue, errors }) => (
        <Form onSubmit={handleSubmit}>
          <FilterGrid>
            {isAdmin && (
              <>
                <Form.Group className="px-1">
                  <Form.Label>Search users</Form.Label>
                  <UserSearch
                    onUserClear={() => {
                      setCurrentPage(1);
                      setFilterRequest({
                        ...filterRequest,
                        startIndex: 0,
                        userId: undefined,
                      });
                      setSelectedUser(undefined);
                    }}
                    onUserSelect={(user: User) => {
                      setCurrentPage(1);
                      setFilterRequest({
                        ...filterRequest,
                        startIndex: 0,
                        userId: user.id,
                      });
                      setSelectedUser(user);
                    }}
                    selectedUser={selectedUser}
                  />
                </Form.Group>
                <Form.Group className="px-1">
                  <Form.Label>Layer:</Form.Label>
                  <LayerDropdown
                    onChange={(e: any) => {
                      setCurrentPage(1);
                      setFilterRequest({
                        ...filterRequest,
                        startIndex: 0,
                        layer: e.target.value,
                      });
                    }}
                    value={filterRequest.layer}
                  />
                </Form.Group>
              </>
            )}
            <LogsAfter>
              <Form.Group>
                <Form.Label>Logs after:</Form.Label>
                <TimeContainer>
                  <Form.Control
                    className="w-50"
                    type="date"
                    value={values.startDate}
                    onChange={handleChange}
                    name="startDate"
                  />
                  <TimeInput
                    className="w-50"
                    value={values.startTime}
                    onChange={(value: string) =>
                      setFieldValue("startTime", value)
                    }
                    name="startTime"
                    hasError={!!errors.startTime}
                  />
                </TimeContainer>
              </Form.Group>
            </LogsAfter>
            <LogBefore>
              <Form.Group className="px-1">
                <Form.Label>Logs before:</Form.Label>
                <TimeContainer>
                  <Form.Control
                    className="w-50"
                    type="date"
                    value={values.endDate}
                    onChange={handleChange}
                    name="endDate"
                  />
                  <TimeInput
                    className="w-50"
                    value={values.endTime}
                    onChange={(value: string) =>
                      setFieldValue("endTime", value)
                    }
                    name="endTime"
                    hasError={!!errors.startTime}
                  />
                </TimeContainer>
              </Form.Group>
            </LogBefore>
            <ApplyRange>
              <Button type="submit">Apply Range</Button>
            </ApplyRange>
            <PageOptions>
              <Form.Label></Form.Label>
              <FormSelect
                className="text-center w-25 mt-2"
                value={filterRequest.count}
                onChange={(e: any) => {
                  setCurrentPage(1);
                  setFilterRequest({
                    ...filterRequest,
                    startIndex: 0,
                    count: e.target.value,
                  });
                }}
                name="count"
              >
                {countOptions.map((countOption, index) => (
                  <option key={index} value={countOption}>
                    {countOption}
                  </option>
                ))}
              </FormSelect>
            </PageOptions>
          </FilterGrid>
        </Form>
      )}
    </Formik>
  );
};

export default RequestHistoryFilters;
